'use client'
import PropertyCard from './PropertyCard'
import { PropertyCard as PropertyCardGrid } from '@/components/PropertyCard'
import AsideFilterComponent from './AsideFilterComponent'
import { Button } from '@/components/ui/button'
import { LayoutGrid, LayoutList, SlidersHorizontal } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/utilities/ui'
import MobilePropertyListingFilterComponent from './MobilePropertyListingFilterComponent'
import { useRouter } from 'next/navigation'

import willayaList from '@/utilities/data/Algeria_Willaya.json'
import cityList from '@/utilities/data/Algeria_Cities.json'

import { PaginationProprety } from '@/components/PaginationProprety'
import Link from 'next/link'
import { Property } from '@/payload-types'
import { useTheme } from '@/providers/Theme'

type PropertyListingPageProps = {
  Properties: any
  searchTerm?: string
  filters?: Record<string, any>
  className?: string
  searchParams?: any
  lang?: 'en' | 'fr' | 'ar' // Added language parameter
  dictionary?: any // Added dictionary parameter
  recommendedSearchesList?: {
    categories: any[],
    initialVisibleItems: number
  }
}

const PropertyCategories = [
  {
    id: 4,
    title: 'Villa',
    value: 'villa',
  },
  {
    id: 2,
    title: 'Apartment',
    value: 'apartment',
  },
  {
    id: 3,
    title: 'Office',
    value: 'office',
  },
  {
    id: 5,
    title: 'Land',
    value: 'land',
  },
]

const roomOptions = [
  { value: 'any', label: 'Any' },
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
  { value: 5, label: '5' },
  { value: 6, label: '5+' },
]

const FeatureSearchList = [
  {
    feature: 'Generator',
    value: 'generator',
  },
  {
    feature: 'Water Tank',
    value: 'waterTank',
  },
  {
    feature: 'Gas Connection',
    value: 'gasConnection',
  },
  {
    feature: 'Garage',
    value: 'garage',
  },
  {
    feature: 'Balcony',
    value: 'balcony',
  },
  {
    feature: 'Elevator',
    value: 'elevator',
  },
  {
    feature: 'Garden',
    value: 'garden',
  },
  {
    feature: 'Terrace',
    value: 'terrace',
  },
  {
    feature: 'Nearby Mosque',
    value: 'nearbyMosque',
  },
  {
    feature: 'Shared Parking',
    value: 'sharedParking',
  },
  {
    feature: 'Air Conditioning',
    value: 'airConditioning',
  },
  {
    feature: 'Heater',
    value: 'heater',
  },

  {
    feature: 'Front Yard',
    value: 'frontYard',
  },
  {
    feature: 'Gym',
    value: 'gym',
  },
  {
    feature: 'Laundry',
    value: 'laundry',
  },
  {
    feature: 'Private Space',
    value: 'privateSpace',
  },
  {
    feature: 'Refrigerator',
    value: 'refrigerator',
  },
  {
    feature: 'Sauna',
    value: 'sauna',
  },
  {
    feature: 'Sea View',
    value: 'seaView',
  },
  {
    feature: 'Water Tank',
    value: 'waterTank',
  },
  {
    feature: 'Wifi',
    value: 'wifi',
  },
]

const PropertyListingPage = ({
  Properties,
  searchTerm,
  filters = {},
  className,
  lang = 'en',
  dictionary = {},
  recommendedSearchesList = {
    categories: [],
    initialVisibleItems: 5
  }
}: PropertyListingPageProps) => {
  const [sortBy, setSortBy] = useState('newest')
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list')
  const [isMobile, setIsMobile] = useState(false)
  const router = useRouter()
  const { theme } = useTheme()

  // Check if RTL
  const isRTL = lang === 'ar'

  // Initialize states
  const [search, setSearch] = useState(searchTerm || '')
  const [listedIn, setListedIn] = useState(filters.listedIn || '')
  const [selectedCategories, setSelectedCategories] = useState<string[]>(filters.categories || [])
  const [selectedWillayas, setSelectedWillayas] = useState<string[]>(filters.wilaya || [])
  const [selectedCities, setSelectedCities] = useState<string[]>(filters.cities || [])

  const [minPrice, setMinPrice] = useState(filters.minPrice || '')
  const [maxPrice, setMaxPrice] = useState(filters.maxPrice || '')
  const [minSurface, setMinSurface] = useState(filters.minSurface || '')
  const [maxSurface, setMaxSurface] = useState(filters.maxSurface || '')
  const [rooms, setRooms] = useState<string[]>(filters.rooms || [])
  const [amenities, setAmenities] = useState<string[]>(filters.amenities || [])
  const [openMobileFilter, setOpenMobileFilter] = useState(false)
  const [isOpenOtherFeatures, setIsOpenOtherFeatures] = useState(false)
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false)
  const [isWillayaPopoverOpen, setIsWillayaPopoverOpen] = useState(false)
  const [isCityPopoverOpen, setIsCityPopoverOpen] = useState(false)
  const [page, setPage] = useState(filters.page)

  // Get cities for selected wilayas
  const availableCities = useMemo(() => {
    return cityList.filter((city) => selectedWillayas.includes(city.wilaya_id))
  }, [selectedWillayas])

  // Remove selected cities that are not in the available cities anymore
  useEffect(() => {
    const validCityIds = availableCities.map((city) => city.id)
    setSelectedCities((prev) => prev.filter((cityId) => validCityIds.includes(cityId)))
  }, [availableCities])

  const firstSortRun = useRef(true)

  useEffect(() => {
    if (firstSortRun.current) {
      firstSortRun.current = false
      return
    }
    updateSearchParams()
  }, [sortBy])
  // Handle form submission - construct URL with all parameters
  const updateSearchParams = () => {
    const params = new URLSearchParams()

    if (search) params.set('search', search)
    
    // TODO REMOVE AFTER TESTING
    // if (listedIn) params.set('listedIn', listedIn)

    if (selectedCategories.length > 0) params.set('categories', selectedCategories.join(','))
    if (selectedWillayas.length > 0) params.set('wilaya', selectedWillayas.join(','))
    if (selectedCities.length > 0) params.set('cities', selectedCities.join(','))
    if (minPrice) params.set('minPrice', minPrice.toString())
    if (maxPrice) params.set('maxPrice', maxPrice.toString())
    if (minSurface) params.set('minSurface', minSurface.toString())
    if (maxSurface) params.set('maxSurface', maxSurface.toString())
    if (rooms.length > 0) params.set('rooms', rooms.join(','))
    if (amenities.length > 0) params.set('amenities', amenities.join(','))

    if (isMobile) setOpenMobileFilter(false)

    params.set('page', '1')
    if(sortBy !== 'newest')
      params.set('sorting', sortBy)

    // Add language parameter to preserve it in URL
    let basePath = lang ? `/${lang}/properties` : '/properties'
    if (listedIn === 'for_sale') basePath += '/for-sale'
    if (listedIn === 'for_rent') basePath += '/for-rent'
    
    const qs = params.toString()
    router.push(qs ? `${basePath}?${qs}` : basePath)

    // REMOVE AFTER TESTING
    // router.push(`${basePath}?${params.toString()}`)
    // router.refresh()
  }

  const updatePagination = () => {
    const paramsPagination = new URLSearchParams(window.location.search)
     // set new page
    if (page > 0) paramsPagination.set('page', page.toString())
    else paramsPagination.delete('page')

    // keep sorting/search/filters already in URL
    let basePath = lang ? `/${lang}/properties` : '/properties'
    if (listedIn === 'for_sale') basePath += '/for-sale'
    if (listedIn === 'for_rent') basePath += '/for-rent'

    const qs = paramsPagination.toString()
    router.push(qs ? `${basePath}?${qs}` : basePath)
  }

  // Filter handlers
  const handleSearchChange = (value) => {
    setSearch(value)
  }

  const handleListedInChange = (value) => {
    setListedIn(value)
  }
  const handleMinPriceChange = (value) => {
    setMinPrice(value)
  }

  const handleMaxPriceChange = (value) => {
    setMaxPrice(value)
  }

  const handleMinSurfaceChange = (value) => {
    setMinSurface(value)
  }

  const handleMaxSurfaceChange = (value) => {
    setMaxSurface(value)
  }

  const handleRoomsChange = (value) => {
    setRooms(value === rooms ? '' : value)
  }
  const toggleCategory = (categoryValue: string) => {
    setSelectedCategories((prev) => {
      if (prev.includes(categoryValue)) {
        // Remove category
        const newCategories = prev.filter((value) => value !== categoryValue)
        return newCategories
      } else {
        // Add category
        return [...prev, categoryValue]
      }
    })
  }

  const toggleAmenity = (amenity: string) => {
    setAmenities((prev) => {
      if (prev.includes(amenity)) {
        return prev.filter((item) => item !== amenity)
      } else {
        return [...prev, amenity]
      }
    })
  }

  // Reset all filters
  const handleReset = () => {
    setSearch('')
    setListedIn('')
    setSelectedCategories([])
    setSelectedWillayas([])
    setSelectedCities([])
    setMinPrice('')
    setMaxPrice('')
    setMinSurface('')
    setMaxSurface('')
    setRooms([])
    setAmenities([])

    if (isMobile) setOpenMobileFilter(false)

    // Navigate to base URL with language
    const basePath = lang ? `/${lang}/properties` : '/properties'
    router.push(basePath)
  }

  // Check if we're on mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    // Initial check
    checkIfMobile()
    // Add event listener
    window.addEventListener('resize', checkIfMobile)
    // Cleanup
    return () => {
      window.removeEventListener('resize', checkIfMobile)
    }
  }, [])

  const translate = {
    filter: dictionary?.filter || 'Filter',
    sortBy: dictionary?.sortBy || 'Sort by:',
    newest: dictionary?.newest || 'Newest',
    priceLowToHigh: dictionary?.priceLowToHigh || 'Price: Low to High',
    priceHighToLow: dictionary?.priceHighToLow || 'Price: High to Low',
    showing: dictionary?.showing || 'Showing',
    of: dictionary?.of || 'of',
    results: dictionary?.results || 'results',
    noPropertiesFound:
      dictionary?.noPropertiesFound || 'No properties found matching your criteria',
    tryAdjusting: dictionary?.tryAdjusting || 'Try adjusting your filters or search term',
    recommendedSearches: dictionary?.recommendedSearches || 'Recommended searches',
    viewMoreText: dictionary?.viewMoreText || 'View more',
    viewLessText: dictionary?.viewLessText || 'View less',
    gridLayout: dictionary?.gridLayout || 'Grid',
    listLayout: dictionary?.listLayout || 'List',
  }

  const translatedPropertyCategories = PropertyCategories.map((category) => ({
    ...category,
    title: dictionary?.propertyCategories?.[category.value] || category.title,
  }))

  const translatedRoomOptions = roomOptions.map((option) => ({
    ...option,
    label: dictionary?.rooms?.[option.value] || option.label,
  }))

  const translatedFeatureSearchList = FeatureSearchList.map((feature) => {
    return {
      ...feature,
      feature: dictionary?.features?.[feature.value] || feature.feature,
    }
  })

  const [showAllRecommendations, setShowAllRecommendations] = useState(false);

  const allSearches = recommendedSearchesList?.categories.flatMap((category: any) => 
    (category.searches || []).map((search: any) => ({
      href: search.searchLink || '#',
      text: search.searchTitle,
    }))
  ) || []


  const visibleSearches = showAllRecommendations 
    ? allSearches 
    : allSearches?.slice(0, recommendedSearchesList?.initialVisibleItems);

  return (
    <>
      <div className={cn('bg-light', className)}>
        <div className="container mx-auto py-8 px-4">
          <div
            className={cn('w-full flex items-start justify-between', isRTL && 'flex-row-reverse')}
          >
            <div className="mb-8"></div>

            <Button
              type="button"
              variant={'ghost'}
              className={cn(
                'text-typography flex w-[97px] h-[40px] text-sm items-center justify-center bg-card rounded-2xl [box-shadow:0px_1px_4px_0px_rgba(24,26,32,0.07)] md:hidden',
                isRTL && 'flex-row-reverse',
              )}
              onClick={() => setOpenMobileFilter(true)}
            >
              <SlidersHorizontal className={cn('h-5 w-5', isRTL ? 'ml-2' : 'mr-2')} />
              {translate.filter}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              {/* Sidebar with Filters */}
              {!isMobile && (
                <AsideFilterComponent
                  className={cn(
                    'hidden bg-card p-4 h-fit md:flex flex-col gap-5 rounded-md',
                    isRTL && 'md:order-1',
                  )}
                  search={search}
                  handleSearchChange={handleSearchChange}
                  selectedCategories={selectedCategories}
                  toggleCategory={toggleCategory}
                  listedIn={listedIn}
                  onListedInChange={handleListedInChange}
                  minPrice={minPrice}
                  onMinPriceChange={handleMinPriceChange}
                  maxPrice={maxPrice}
                  onMaxPriceChange={handleMaxPriceChange}
                  minSurface={minSurface}
                  onMinSurfaceChange={handleMinSurfaceChange}
                  maxSurface={maxSurface}
                  onMaxSurfaceChange={handleMaxSurfaceChange}
                  rooms={rooms}
                  onRoomsChange={handleRoomsChange}
                  amenities={amenities}
                  toggleAmenity={toggleAmenity}
                  updateSearchParams={updateSearchParams}
                  onReset={handleReset}
                  willayaList={willayaList}
                  setSelectedWillayas={setSelectedWillayas}
                  selectedWillayas={selectedWillayas}
                  isWillayaPopoverOpen={isWillayaPopoverOpen}
                  setIsWillayaPopoverOpen={setIsWillayaPopoverOpen}
                  citiesList={availableCities}
                  setSelectedCities={setSelectedCities}
                  selectedCities={selectedCities}
                  isCityPopoverOpen={isCityPopoverOpen}
                  setIsCityPopoverOpen={setIsCityPopoverOpen}
                  isOpenOtherFeatures={isOpenOtherFeatures}
                  setIsOpenOtherFeatures={setIsOpenOtherFeatures}
                  showAdvancedSearch={showAdvancedSearch}
                  setShowAdvancedSearch={setShowAdvancedSearch}
                  PropertyCategories={translatedPropertyCategories}
                  roomOptions={translatedRoomOptions}
                  FeatureSearchList={translatedFeatureSearchList}
                  dictionary={dictionary}
                  lang={lang}
                />
              )}
              {/* RECOMMENDED SEARCH */}
              <div>
                <h3 className="bg-gray-200 text-gray-600 py-2 px-4 text-sm mt-6 rounded-sm">
                  {translate.recommendedSearches}
                </h3>
                <ul className="text-xs text-[#00994C] flex flex-col gap-3 px-4 py-3">
                  {visibleSearches?.map((search, index) => (
                    <li key={index} className="hover:underline cursor-pointer">
                      <Link href={search.href}>
                        {search.text}
                      </Link>
                    </li>
                  ))}
                  
                  {/* View More/Less Button */}
                  {allSearches.length > recommendedSearchesList?.initialVisibleItems && (
                    <li>
                      <button 
                        className="text-gray-700 font-semibold hover:underline focus:outline-none"
                        onClick={() => setShowAllRecommendations(!showAllRecommendations)}
                      >
                        {showAllRecommendations ? translate.viewLessText : translate.viewMoreText}
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            </div>

            {/* Property Listing */}
            <div className={cn('md:col-span-3', isRTL && 'md:order-2')}>
              {/* Top Bar */}
              <div
                className={cn(
                  'flex flex-col sm:ltr:flex-row rtl:flex-row-reverse justify-between items-start sm:items-center mb-6',
                )}
              >
                <div className={cn('flex items-center gap-2 mb-6')}>
                  <div
                    role="button"
                    onClick={() => setViewMode('list')}
                    className={cn(
                      'px-3 py-2 rounded-lg text-sm border order-3 hover:bg-card',
                      viewMode === 'list' ? 'bg-card' : 'bg-transparent'
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <LayoutList size={16} color={theme === 'dark' ? '#fff' : '#000'} />
                      <span className={theme === 'dark' ? 'text-white' : ''}>{translate.listLayout}</span>
                    </div>
                  </div>

                  <div
                    role="button"
                    onClick={() => setViewMode('grid')}
                    className={cn(
                      'px-3 py-2 rounded-lg text-sm border',
                      viewMode === 'grid' ? 'bg-card' : 'bg-transparent hover:bg-card'
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <LayoutGrid size={16} color={theme === 'dark' ? '#fff': '#000'} />
                      <span className={theme === 'dark' ? 'text-white' : ''}>{translate.gridLayout}</span>
                    </div>
                  </div>
                </div>
                <div className="mb-4 sm:mb-0">
                  <p className={cn('text-typography text-sm', isRTL && 'text-right')}>
                    {translate.showing} {Properties.hits.length || 0} {translate.of}{' '}
                    {Properties.totalHits || 0} {translate.results}
                  </p>
                </div>
                <div className={cn('flex items-center')}>
                  <span className={cn('text-typography text-sm ml-2 rtl:mr-2')}>
                    {translate.sortBy}
                  </span>
                  <Select
                    onValueChange={(value) => {
                      setSortBy(value)
                    }}
                  >
                    <SelectTrigger
                      className={cn(
                        'w-36 border-0 rtl:justify-end justify-start text-sm ring-offset-0 underline focus:border-0 focus:ring-0 bg-transparent',
                        isRTL && 'text-right',
                      )}
                    >
                      <SelectValue placeholder={translate.newest} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="newest">{translate.newest}</SelectItem>
                      <SelectItem value="propertyDescription.price:asc">
                        {translate.priceLowToHigh}
                      </SelectItem>
                      <SelectItem value="propertyDescription.price:desc">
                        {translate.priceHighToLow}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* Properties List */}
              <div className="space-y-6">
                {Properties.hits && Properties.hits.length > 0 ? (
                  viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      {Properties.hits.map((property: Property) => (
                        <PropertyCardGrid
                          lang={lang} 
                          key={property.id} 
                          property={property} 
                          dictionary={dictionary} 
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {Properties.hits.map((property: Property) => (
                        <PropertyCard
                          key={property.id}
                          property={property}
                          lang={lang}
                          dictionary={dictionary}
                        />
                      ))}
                    </div>
                  )
                ) : (
                  <div className={cn('text-center py-12', isRTL && 'text-right')}>
                    <p className="text-typography text-xl">{translate.noPropertiesFound}</p>
                    <p className="text-typography mt-2">{translate.tryAdjusting}</p>
                  </div>
                )}
              </div>
              {/* Pagination */}
              <>
                <PaginationProprety
                  updatePagination={updatePagination}
                  page={page}
                  setPage={setPage}
                  totalPages={Properties.totalPages}
                  dictionary={dictionary}
                />
              </>
            </div>
          
          </div>

          {/* Mobile Property Listing Filter Component */}
          {/* //! Don't Delete isMobile In mobile view because we cant use wilaya or cities multiple select */}
          {isMobile && (
            <MobilePropertyListingFilterComponent
              className={'flex-grow p-6 overflow-y-auto flex flex-col gap-6 bg-light'}
              open={openMobileFilter}
              setOpen={setOpenMobileFilter}
              search={search}
              handleSearchChange={handleSearchChange}
              selectedCategories={selectedCategories}
              toggleCategory={toggleCategory}
              listedIn={listedIn}
              onListedInChange={handleListedInChange}
              minPrice={minPrice}
              onMinPriceChange={handleMinPriceChange}
              maxPrice={maxPrice}
              onMaxPriceChange={handleMaxPriceChange}
              minSurface={minSurface}
              onMinSurfaceChange={handleMinSurfaceChange}
              maxSurface={maxSurface}
              onMaxSurfaceChange={handleMaxSurfaceChange}
              rooms={rooms}
              onRoomsChange={handleRoomsChange}
              amenities={amenities}
              toggleAmenity={toggleAmenity}
              updateSearchParams={updateSearchParams}
              onReset={handleReset}
              willayaList={willayaList}
              setSelectedWillayas={setSelectedWillayas}
              selectedWillayas={selectedWillayas}
              isWillayaPopoverOpen={isWillayaPopoverOpen}
              setIsWillayaPopoverOpen={setIsWillayaPopoverOpen}
              citiesList={availableCities}
              setSelectedCities={setSelectedCities}
              selectedCities={selectedCities}
              isCityPopoverOpen={isCityPopoverOpen}
              setIsCityPopoverOpen={setIsCityPopoverOpen}
              isOpenOtherFeatures={isOpenOtherFeatures}
              setIsOpenOtherFeatures={setIsOpenOtherFeatures}
              showAdvancedSearch={showAdvancedSearch}
              setShowAdvancedSearch={setShowAdvancedSearch}
              PropertyCategories={translatedPropertyCategories}
              roomOptions={translatedRoomOptions}
              FeatureSearchList={translatedFeatureSearchList}
              dictionary={dictionary}
              lang={lang}
            />
          )}
        </div>
      </div>
    </>
  )
}

export default PropertyListingPage
